import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEarlyAccess } from '../EarlyAccessContext';
import ContactForm from '../components/ContactForm';
import NetworkVisual from '../components/NetworkVisual';

function ProductPage() {
  const openEarlyAccess = useEarlyAccess();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);
  return (
    <>
      <div className="product-hero">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="hero-badge">Launching July–August 2026 — Limited to 5 Pilot Customers</span>
              <h1 style={{ fontSize: '48px', color: '#fff', fontWeight: 700, lineHeight: 1.2, marginBottom: '20px' }}>
                Intelligent Network{' '}
                <span className="text-gradient">Monitoring</span>{' '}
                for Modern Teams
              </h1>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '18px', marginBottom: '24px' }}>
                Netwatch provides comprehensive visibility into your entire network. Automatically discover every device,
                monitor health in real time, and manage access with enterprise-grade security — across 20+ vendor families.
                Our MVP launches July–August 2026. We're looking for 5 pilot customers to shape the future of network monitoring with us.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <span className="integration-badge"><i className="fas fa-check-circle"></i> Cisco</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> Juniper</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> Fortinet</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> Palo Alto</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> Checkpoint</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> FortiGate</span>
                <span className="integration-badge"><i className="fas fa-check-circle"></i> 15+ more</span>
              </div>
              <div className="banner-buttons">
                <button onClick={openEarlyAccess} className="btn-primary-gradient">Apply for Early Access</button>
                <a href="#demo-form" className="btn-outline-white">Request Demo</a>
              </div>
            </div>
            <div className="col-lg-6">
              <NetworkVisual variant="hero" />
            </div>
          </div>
        </div>
      </div>

      <div className="sec-padding" style={{ background: '#f8f9fc' }} id="features">
        <div className="container">
          <div className="text-center mb-4">
            <span className="section-subtitle">Platform Overview</span>
          </div>
          <h2 className="head-main after-border center">One Dashboard for Your Entire Network</h2>
          <div className="row align-items-center mt-5">
            <div className="col-lg-7">
              <NetworkVisual variant="dashboard" />
            </div>
            <div className="col-lg-5">
              <div style={{ paddingLeft: '30px' }}>
                <h3 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '16px', color: '#1a1a2e' }}>See Everything, Miss Nothing</h3>
                <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                  Netwatch aggregates data from every device and platform on your network into a unified, real-time dashboard.
                  From routers and switches to firewalls and wireless APs — monitor it all in one place.
                </p>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {[
                    'Auto-discovered network topology maps',
                    'Cross-vendor unified monitoring view',
                    'Role-based dashboards per user tier',
                    'Device health and capability tracking',
                    'DNS, DHCP, and IP address management'
                  ].map((item, i) => (
                    <li key={i} style={{ padding: '8px 0', color: '#374151', fontSize: '15px' }}>
                      <i className="fas fa-check-circle" style={{ color: '#0e63ff', marginRight: '10px' }}></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-section" style={{ padding: '60px 0' }}>
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="text-center mb-4">
            <span className="section-subtitle" style={{ color: 'rgba(255,255,255,0.6)' }}>Core Capabilities</span>
          </div>
          <h2 className="head-main after-border center text-white">Everything You Need to Monitor Your Network</h2>
          <div className="row g-4 mt-3">
            <div className="col-lg-4 col-md-6">
              <div className="feature-card-dark">
                <div className="feature-icon"><i className="fas fa-search-location"></i></div>
                <h3>Network Discovery</h3>
                <p>Automatically scan and discover every device on your network — routers, switches, firewalls, servers, wireless APs, printers, and more. Netwatch identifies vendor, model, OS, interfaces, and capabilities without manual inventory.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card-dark">
                <div className="feature-icon"><i className="fas fa-user-shield"></i></div>
                <h3>Role-Based Access Control</h3>
                <p>Five role tiers — Super Admin, Admin, Operator, Viewer, and Guest — with granular permissions across every resource. The interface adapts to each user's role, showing only the controls and data they're authorized to access.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card-dark">
                <div className="feature-icon"><i className="fas fa-tachometer-alt"></i></div>
                <h3>Real-time Monitoring</h3>
                <p>Live dashboards with instant visibility into device health, DNS query performance, DHCP lease status, IP address utilization, and network topology — all updated in real time across your infrastructure.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card-dark">
                <div className="feature-icon"><i className="fas fa-bell"></i></div>
                <h3>Smart Alerts &amp; Notifications</h3>
                <p>Set custom thresholds and receive alerts via email, Slack, PagerDuty, or webhooks. Intelligent anomaly detection catches issues early so you can respond before they impact users.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card-dark">
                <div className="feature-icon"><i className="fas fa-plug"></i></div>
                <h3>Multi-vendor Support</h3>
                <p>Works with 20+ vendor families out of the box — Cisco, Juniper, Palo Alto, Checkpoint, FortiGate, Arista, Microsoft, HP, Dell, VMware, MikroTik, and more. Firewall monitoring via SNMP. No vendor lock-in, ever.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sec-padding" id="integrations">
        <div className="container">
          <div className="text-center mb-4">
            <span className="section-subtitle">Integrations</span>
          </div>
          <h2 className="head-main after-border center">Works With Your Existing Stack</h2>
          <p className="text-center mb-5" style={{ maxWidth: '600px', margin: '0 auto 40px', color: '#6b7280' }}>
            Netwatch integrates natively with the network platforms and tools you already use.
          </p>
          <div className="row g-4 justify-content-center">
            {[
              { name: 'Cisco', icon: 'fas fa-network-wired', desc: 'Full discovery and monitoring for Cisco routers, switches, and firewalls' },
              { name: 'Juniper', icon: 'fas fa-project-diagram', desc: 'Native support for Juniper Networks routing and switching platforms' },
              { name: 'Palo Alto', icon: 'fas fa-shield-alt', desc: 'SNMP-based monitoring for Palo Alto firewalls — health, interfaces, and traffic visibility' },
              { name: 'Checkpoint', icon: 'fas fa-lock', desc: 'SNMP monitoring for Checkpoint security gateways and management servers' },
              { name: 'FortiGate', icon: 'fas fa-fire-alt', desc: 'SNMP-based FortiGate firewall monitoring for device health, policies, and uptime' },
              { name: 'Slack / PagerDuty', icon: 'fab fa-slack', desc: 'Real-time alert notifications and incident escalation to your team' },
            ].map((item, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <div className="feature-card text-center">
                  <div className="feature-icon" style={{ margin: '0 auto 20px' }}>
                    <i className={item.icon}></i>
                  </div>
                  <h3>{item.name}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="cta-section text-center" id="contact">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <h2>Join Our Early Access Program</h2>
          <p>Launching July–August 2026 — we're selecting 5 pilot customers to get hands-on access to Netwatch, provide feedback, and help shape the product. Interested? Let's talk.</p>
          <div className="banner-buttons" style={{ justifyContent: 'center' }}>
            <button onClick={openEarlyAccess} className="btn-primary-gradient">Apply Now</button>
            <a href="#demo-form" className="btn-outline-white">Talk to Sales</a>
          </div>
        </div>
      </div>

      <ContactForm
        title="Apply for Early Access"
        description="Our MVP launches July–August 2026. We're selecting 5 pilot customers to test Netwatch and help shape its future. Fill out the form below and our team will be in touch."
        earlyAccess={true}
      />
    </>
  );
}

export default ProductPage;
